import { PageOverviewBanner } from '@/components';
import Adsense from '@/components/Adsense';
import { AdsenseSectionWrapper } from '@/components/Adsense/Adsense.styles';
import SEOSection from '@/components/SEOSection';
import Wrapper from '@/components/Wrapper';
import { ContentTypeName, seoPageAdSlots } from '@/constants';
import {
  type ButtonProps,
  type ILandingPage,
  LeadGenBannerVariant,
  type SectionItem,
  SectionThemeKey,
  SeoPageAdType,
  Widget,
  WrapperBackgroundColor,
} from '@/interfaces';
import { capitalize, extractCarrierSlug } from '@/utils';
import type { Document as ContentfulDocument } from '@contentful/rich-text-types';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import SectionListRenderer from '../SectionListRenderer';

const TRACKING_SOLUTION_ICONS = [
  '/images/icons/tracking-solution-1.svg',
  '/images/icons/tracking-solution-2.svg',
  '/images/icons/tracking-solution-3.svg',
  '/images/icons/tracking-solution-4.svg',
];

interface AdsenseWrapperProps {
  $bgColor?: WrapperBackgroundColor;
}

const AdsenseWrapper = styled.div<AdsenseWrapperProps>`
  ${({ theme, $bgColor }) => `
    ${AdsenseSectionWrapper} {
      background: ${
        $bgColor === WrapperBackgroundColor.grey ? theme.colors.pageBackground : theme.colors.highEmphasisWhite
      };
    }
  `}
`;

interface SEOPageRendererProps {
  data: ILandingPage;
}

const SEOPageRenderer: React.FC<SEOPageRendererProps> = ({ data }) => {
  const { sectionList = [], pageOverviewBanner, pageOverviewBannerCtaButtons, faq, trackingSolution } = data;
  const router = useRouter();
  const { slug } = router.query as { slug: string };
  const carrierSlug = extractCarrierSlug(data, slug);

  const filteredSectionList = sectionList.reduce<SectionItem[]>((filtered, section) => {
    const typeName = capitalize(section.__typename) as ContentTypeName;

    if (typeName === ContentTypeName.PageOverviewBanner) return filtered;
    if (typeName === ContentTypeName.LeadGenBanner) {
      filtered.push({ ...section, variant: LeadGenBannerVariant.FULL_WIDTH });
      return filtered;
    }
    filtered.push(section);
    return filtered;
  }, []);

  const filteredTrackingSeoList =
    trackingSolution?.seoList
      ?.filter((item) => !!item?.title)
      .map((item, i) => ({
        ...item,
        icon: { url: TRACKING_SOLUTION_ICONS[i], contentType: 'image/svg+xml' },
      })) ?? [];

  const shouldRenderFaq = faq?.title && faq.content;
  const shouldRenderTrackingSolution = trackingSolution?.title && filteredTrackingSeoList.length > 0;

  return (
    <>
      {pageOverviewBanner && (
        <PageOverviewBanner
          widget={Widget.Tracking}
          title={pageOverviewBanner.title}
          titleMobile={pageOverviewBanner.titleMobile ?? pageOverviewBanner.title}
          subTitle={pageOverviewBanner.subTitle}
          subText={pageOverviewBanner.subText}
          subTextMobile={pageOverviewBanner.subTextMobile}
          buttons={pageOverviewBannerCtaButtons as ButtonProps[]}
          theme={pageOverviewBanner.theme ?? SectionThemeKey.Dark}
          heroAsset={{ url: '/images/overview_banner.png', contentType: 'image/png', description: 'Overview banner' }}
          carrierSlug={carrierSlug}
          priority
        />
      )}
      <AdsenseWrapper $bgColor={filteredSectionList[0]?.backgroundColor as WrapperBackgroundColor}>
        <Adsense className='seo-ads seo-ads-top' dataAdSlot={seoPageAdSlots[SeoPageAdType.TOP]} />
      </AdsenseWrapper>

      <SectionListRenderer sectionList={filteredSectionList} />

      <AdsenseWrapper $bgColor={WrapperBackgroundColor.white}>
        <Adsense className='seo-ads seo-ads-middle' dataAdSlot={seoPageAdSlots[SeoPageAdType.MIDDLE]} />
      </AdsenseWrapper>
      {shouldRenderFaq && (
        <>
          <Wrapper
            title={faq.title}
            content={{
              json: faq.content as ContentfulDocument,
              links: { assets: { block: [] }, entries: { block: [] } },
            }}
            backgroundColor={WrapperBackgroundColor.white}
          />
        </>
      )}
      <AdsenseWrapper $bgColor={WrapperBackgroundColor.white}>
        <Adsense className='seo-ads seo-ads-bottom' dataAdSlot={seoPageAdSlots[SeoPageAdType.BOTTOM]} />
      </AdsenseWrapper>

      {shouldRenderTrackingSolution && (
        <SEOSection
          title={trackingSolution.title as string}
          backgroundColor={WrapperBackgroundColor.grey}
          seoList={filteredTrackingSeoList}
        />
      )}
    </>
  );
};

export default SEOPageRenderer;
